/**
 * SupportAnalytics — Session-35 rebuild.
 *
 * 3 tabs only: Total Sessions / Resolved / Unresolved
 * Per customer = 1 row (badge shows Total · Resolved · Unresolved counts).
 * Expand row → all sessions listed with date+time. Click a session → jumps
 * to that room's chat.
 *
 * Admin actions (super_admin only):
 *   - Clear All in current tab   (password gate)
 *   - Clear Old Data (date range) (password gate)
 *   - Refresh button that ACTUALLY refetches instantly.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowClockwise, CaretDown, CaretRight, ChatCircleDots,
  Trash, Calendar, User, ShieldCheck, LockKey,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import MBSetupBadge from '../components/MBSetupBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: 'total',      label: 'Total Sessions', color: 'indigo' },
  { key: 'resolved',   label: 'Resolved',       color: 'emerald' },
  { key: 'unresolved', label: 'Unresolved',     color: 'rose' },
];

const fmtIST = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const SupportAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(() => readInstantCache('support_analytics', user?.id) || null);
  const [tab, setTab] = useState('total');
  // Point 2 — each tab (Total/Resolved/Unresolved) keeps its OWN scroll.
  const bodyRef = useRef(null);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    try {
      const saved = parseInt(sessionStorage.getItem(`support_analytics_scroll_${tab}`) || '0', 10);
      requestAnimationFrame(() => { if (bodyRef.current) bodyRef.current.scrollTop = saved; });
    } catch (_) {}
  }, [tab]);
  const switchTab = (key) => {
    try {
      if (bodyRef.current) sessionStorage.setItem(`support_analytics_scroll_${tab}`, String(bodyRef.current.scrollTop));
    } catch (_) {}
    setTab(key);
  };
  const [loading, setLoading] = useState(() => !readInstantCache('support_analytics', user?.id));
  const [expandedIds, setExpandedIds] = useState({});
  const [passwordGate, setPasswordGate] = useState(null); // {action, scope, from, to}
  const [passwordInput, setPasswordInput] = useState('');
  const [clearDateRange, setClearDateRange] = useState({ from: '', to: '' });
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  // Session-36 — bulk mark-solved selection (unresolved tab only)
  const [selectedSessionIds, setSelectedSessionIds] = useState(new Set());
  const [markSolvedBusy, setMarkSolvedBusy] = useState(false);

  const load = useCallback(async (opts = {}) => {
    const { retry = 1 } = opts;
    try {
      if (!readInstantCache('support_analytics', user?.id)) setLoading(true);
      const res = await axios.get(`${API}/support/analytics`, {
        params: { user_id: user?.id, nocache: Date.now() },
        timeout: 12000,
      });
      setData(res.data || null);
      writeInstantCache('support_analytics', user?.id, res.data || null);
      setLoading(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        toast.error(detail || 'Only super_admin can view support analytics');
        navigate('/admin-settings');
        setLoading(false);
        return;
      }
      // POINT 3 — timeout / network hiccup: one silent retry so a single hung
      // request never leaves the screen stuck in a loading spinner. If the
      // retry also fails, fail gracefully and let the user retry manually.
      if (retry > 0) {
        setTimeout(() => load({ retry: retry - 1 }), 800);
        return;
      }
      setLoading(false);
      toast.error('Failed to load support analytics — tap refresh to retry');
    }
  }, [user?.id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Realtime — refresh when a support session is created/resolved
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const toggleExpand = (cid) => setExpandedIds((p) => ({ ...p, [cid]: !p[cid] }));

  // Filter customers per tab — hide customers who have 0 sessions for that scope
  const visibleCustomers = useMemo(() => {
    if (!data?.by_customer) return [];
    return data.by_customer.filter((c) => {
      if (tab === 'total') return (c.counts?.total || 0) > 0;
      if (tab === 'resolved') return (c.counts?.resolved || 0) > 0;
      if (tab === 'unresolved') return (c.counts?.unresolved || 0) > 0;
      return true;
    });
  }, [data, tab]);

  // Sessions filtered per tab (used inside expanded row)
  const filterSessionsForTab = (sessions) => {
    if (tab === 'resolved') return sessions.filter((s) => s.resolved === true);
    if (tab === 'unresolved') return sessions.filter((s) => s.resolved !== true);
    return sessions;
  };

  const openChat = (session) => {
    if (!session?.room_id) return;
    // Jump to the chat; pass `state.from` so AdminChat's back button returns
    // here instead of the default admin-dashboard.
    navigate(`/admin-chat/${session.room_id}`, {
      state: { from: '/admin/support-analytics' }
    });
  };

  const runClear = async () => {
    if (!passwordInput) { toast.error('Admin password required'); return; }
    const body = {
      actor_id: user?.id,
      password: passwordInput,
      scope: passwordGate.scope || 'all',
    };
    if (passwordGate.action === 'date_range') {
      body.from_date = passwordGate.from;
      body.to_date = passwordGate.to;
    }
    if ((passwordGate.scope || 'all') === 'all' && passwordGate.action !== 'date_range') {
      body.confirm_all = true;
    }
    try {
      const res = await axios.post(`${API}/support/analytics/clear`, body);
      toast.success(`Cleared ${res.data?.deleted || 0} sessions`);
      setPasswordGate(null);
      setPasswordInput('');
      setShowDateRangeModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed');
    }
  };

  const openClearGate = (scope) => {
    setPasswordInput('');
    setPasswordGate({ action: 'scope_clear', scope });
  };

  const submitDateRange = () => {
    if (!clearDateRange.from && !clearDateRange.to) {
      toast.error('Pick at least one date');
      return;
    }
    setShowDateRangeModal(false);
    setPasswordInput('');
    setPasswordGate({
      action: 'date_range',
      scope: 'all',
      from: clearDateRange.from,
      to: clearDateRange.to,
    });
  };

  // Session-36 — Bulk mark-solved helpers (only used in Unresolved tab)
  const toggleSelectSession = (id) => {
    setSelectedSessionIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const allUnresolvedIds = useMemo(() => {
    if (tab !== 'unresolved') return [];
    const ids = [];
    (data?.by_customer || []).forEach((c) => {
      (c.sessions || []).forEach((s) => {
        if (s.resolved !== true) ids.push(s.id);
      });
    });
    return ids;
  }, [tab, data]);

  const selectAllUnresolved = () => setSelectedSessionIds(new Set(allUnresolvedIds));
  const clearSelection = () => setSelectedSessionIds(new Set());

  const markSelectedSolved = async () => {
    if (selectedSessionIds.size === 0) {
      toast.error('Koi session select nahi kiya');
      return;
    }
    setMarkSolvedBusy(true);
    try {
      const res = await axios.post(`${API}/support/analytics/mark-solved-bulk`, {
        actor_id: user?.id,
        session_ids: Array.from(selectedSessionIds),
      });
      toast.success(`${res.data?.modified || 0} sessions marked solved`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk mark solved failed');
    } finally { setMarkSolvedBusy(false); }
  };

  const markAllUnresolvedSolved = async () => {
    setMarkSolvedBusy(true);
    try {
      const res = await axios.post(`${API}/support/analytics/mark-solved-bulk`, {
        actor_id: user?.id,
        all_unresolved: true,
      });
      toast.success(`${res.data?.modified || 0} sessions marked solved`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk mark all solved failed');
    } finally { setMarkSolvedBusy(false); }
  };

  const summary = data?.summary || { total: 0, resolved: 0, unresolved: 0, customers: 0 };

  return (
    <div className="fixed inset-0 bg-slate-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-30 shadow-sm flex-shrink-0">
        <div className="max-w-5xl mx-auto flex items-center gap-2 px-3 py-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-slate-100 rounded-full"
            data-testid="support-analytics-back"
          ><ArrowLeft size={20} /></button>
          <div className="flex-1">
            <h1 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <ChatCircleDots size={20} className="text-indigo-600" weight="fill" />
              Support Analytics
            </h1>
            <p className="text-[11px] text-slate-500">Admin-only · Real-time</p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="p-2 rounded-full bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white"
            title="Refresh"
            data-testid="support-analytics-refresh"
          ><ArrowClockwise size={18} weight="bold" className={loading ? 'animate-spin' : ''} /></button>
        </div>

        {/* Tabs */}
        <div className="max-w-5xl mx-auto flex gap-1 px-3 pb-1 overflow-x-auto">
          {TABS.map((t) => {
            const active = tab === t.key;
            const cnt =
              t.key === 'total' ? summary.total :
              t.key === 'resolved' ? summary.resolved :
              summary.unresolved;
            return (
              <button
                key={t.key}
                onClick={() => switchTab(t.key)}
                className={`flex-1 whitespace-nowrap px-3 py-2 text-xs font-bold rounded-t-lg border-b-2 transition ${
                  active
                    ? `text-${t.color}-700 border-${t.color}-500 bg-${t.color}-50`
                    : 'text-slate-500 border-transparent hover:bg-slate-100'
                }`}
                data-testid={`support-analytics-tab-${t.key}`}
              >
                {t.label} <span className="ml-1 opacity-70">({cnt})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div ref={bodyRef} className="flex-1 overflow-y-auto max-w-5xl mx-auto px-3 py-4">
        {/* Actions bar */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="text-[12px] text-slate-600 mr-auto">
            <b>{visibleCustomers.length}</b> customer{visibleCustomers.length === 1 ? '' : 's'} · <b>{summary.customers}</b> total
          </div>
          {tab === 'unresolved' && (
            <>
              <button
                onClick={selectedSessionIds.size === allUnresolvedIds.length ? clearSelection : selectAllUnresolved}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-white border border-emerald-300 hover:bg-emerald-50 flex items-center gap-1"
                data-testid="support-analytics-select-all"
              >
                {selectedSessionIds.size === allUnresolvedIds.length && allUnresolvedIds.length > 0 ? '☑ Deselect All' : '☐ Select All'}
                {selectedSessionIds.size > 0 && <span className="ml-1 text-emerald-700">({selectedSessionIds.size})</span>}
              </button>
              <button
                onClick={selectedSessionIds.size > 0 ? markSelectedSolved : markAllUnresolvedSolved}
                disabled={markSolvedBusy}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white flex items-center gap-1 disabled:opacity-50"
                data-testid="support-analytics-mark-solved"
              >
                {markSolvedBusy ? '…' : '✓'} Mark {selectedSessionIds.size > 0 ? 'Selected' : 'All'} Solved
              </button>
            </>
          )}
          <button
            onClick={() => setShowDateRangeModal(true)}
            className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 flex items-center gap-1"
            data-testid="support-analytics-clear-date-range"
          ><Calendar size={12} weight="bold" /> Clear Old Data</button>
          <button
            onClick={() => openClearGate(tab === 'total' ? 'all' : tab)}
            className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white flex items-center gap-1"
            data-testid={`support-analytics-clear-${tab}`}
          ><Trash size={12} weight="bold" /> Clear {tab === 'total' ? 'All' : (tab === 'resolved' ? 'Resolved' : 'Unresolved')}</button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
        ) : visibleCustomers.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400">
            No {tab} sessions.
          </div>
        ) : (
          <div className="space-y-2">
            {visibleCustomers.map((c) => {
              const expanded = !!expandedIds[c.customer_id];
              const filtered = filterSessionsForTab(c.sessions || []);
              return (
                <div
                  key={c.customer_id}
                  className="bg-white rounded-lg border border-slate-200 shadow-sm"
                  data-testid={`support-analytics-customer-${c.customer_id}`}
                >
                  <button
                    onClick={() => toggleExpand(c.customer_id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg text-left"
                    data-testid={`support-analytics-expand-${c.customer_id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                      {(c.customer_name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800 text-sm truncate max-w-[200px]" title={c.customer_name}>
                          {c.customer_name}
                        </span>
                        {c.is_priority && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300 font-bold uppercase">VIP</span>
                        )}
                        {c.mb_setup && <MBSetupBadge value={c.mb_setup} />}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-indigo-700">Total: {c.counts?.total || 0}</span>
                        <span className="text-emerald-700">Resolved: {c.counts?.resolved || 0}</span>
                        <span className="text-rose-700">Unresolved: {c.counts?.unresolved || 0}</span>
                        {c.last_at && <span className="text-slate-400">· last {fmtIST(c.last_at)}</span>}
                      </div>
                    </div>
                    <div className="text-slate-400 flex-shrink-0">
                      {expanded ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                    </div>
                  </button>

                  {expanded && (
                    <div className="border-t border-slate-100 divide-y divide-slate-100">
                      {filtered.length === 0 ? (
                        <div className="p-4 text-center text-xs text-slate-400">No {tab} sessions for this customer</div>
                      ) : filtered.map((s) => {
                        const isSelected = selectedSessionIds.has(s.id);
                        const canSelect = tab === 'unresolved' && s.resolved !== true;
                        return (
                        <div
                          key={s.id}
                          className={`w-full flex items-center gap-3 p-2.5 pl-5 hover:bg-indigo-50 text-left transition ${isSelected ? 'bg-emerald-50' : ''}`}
                          data-testid={`support-analytics-session-${s.id}`}
                        >
                          {canSelect && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => { e.stopPropagation(); toggleSelectSession(s.id); }}
                              className="w-4 h-4 rounded border-2 border-emerald-500 accent-emerald-500 flex-shrink-0"
                              data-testid={`support-analytics-check-${s.id}`}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <button
                            onClick={() => openChat(s)}
                            className="flex items-center gap-3 flex-1 min-w-0 text-left"
                          >
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              s.resolved === true ? 'bg-emerald-500'
                                : s.closed ? 'bg-slate-400' : 'bg-rose-500 animate-pulse'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-[12px] text-slate-700 font-medium">
                                {s.resolved === true ? '✓ Resolved' : (s.closed ? 'Closed' : '⏳ Pending')}
                                {s.close_reason && <span className="text-slate-400 ml-1">({s.close_reason})</span>}
                                {(s.options_selected || []).length > 0 && (
                                  <span className="text-slate-500 ml-2 text-[10px]">
                                    · {s.options_selected.map(o => o.label || o.key).slice(0, 2).join(', ')}
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-400 mt-0.5">
                                Started {fmtIST(s.started_at)}
                                {s.last_activity_at && s.last_activity_at !== s.started_at && (
                                  <span> · Last activity {fmtIST(s.last_activity_at)}</span>
                                )}
                              </div>
                            </div>
                            <span className="text-[10px] font-bold text-indigo-600 flex items-center gap-0.5 flex-shrink-0">
                              Open chat →
                            </span>
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Password gate modal */}
      {passwordGate && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-3" onClick={() => setPasswordGate(null)}>
          <div className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center">
                <LockKey size={18} weight="fill" className="text-rose-600" />
              </div>
              <div>
                <div className="font-bold text-slate-800 text-sm">Confirm with Admin Password</div>
                <div className="text-[11px] text-slate-500">
                  {passwordGate.action === 'date_range'
                    ? `Clear sessions from ${passwordGate.from || 'any'} → ${passwordGate.to || 'now'}`
                    : `Clear ${passwordGate.scope === 'all' ? 'ALL' : passwordGate.scope} sessions`}
                </div>
              </div>
            </div>
            <input
              type="password"
              placeholder="Enter admin password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runClear()}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
              autoFocus
              data-testid="support-analytics-password-input"
            />
            <div className="flex gap-2 mt-3">
              <button onClick={() => { setPasswordGate(null); setPasswordInput(''); }}
                className="flex-1 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={runClear}
                className="flex-1 py-2 text-xs font-bold bg-rose-500 hover:bg-rose-600 text-white rounded-lg"
                data-testid="support-analytics-password-confirm"
              >Confirm & Clear</button>
            </div>
          </div>
        </div>
      )}

      {/* Date-range modal */}
      {showDateRangeModal && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-3" onClick={() => setShowDateRangeModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <Calendar size={20} className="text-indigo-600" weight="fill" />
              <div>
                <div className="font-bold text-slate-800 text-sm">Clear Old Data</div>
                <div className="text-[11px] text-slate-500">Sessions in this date range will be deleted</div>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-600 block mb-1">From (IST)</label>
                <input type="date" value={clearDateRange.from}
                  onChange={(e) => setClearDateRange((p) => ({ ...p, from: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-600 block mb-1">To (IST)</label>
                <input type="date" value={clearDateRange.to}
                  onChange={(e) => setClearDateRange((p) => ({ ...p, to: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => {
                  const to = new Date().toISOString().split('T')[0];
                  const from = new Date(Date.now() - 15 * 86400000).toISOString().split('T')[0];
                  setClearDateRange({ from, to });
                }} className="flex-1 text-[10px] py-1.5 rounded bg-slate-100 hover:bg-slate-200 font-semibold">Last 15 days</button>
                <button onClick={() => {
                  const to = new Date().toISOString().split('T')[0];
                  const from = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
                  setClearDateRange({ from, to });
                }} className="flex-1 text-[10px] py-1.5 rounded bg-slate-100 hover:bg-slate-200 font-semibold">Last 30 days</button>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowDateRangeModal(false)}
                className="flex-1 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={submitDateRange}
                className="flex-1 py-2 text-xs font-bold bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg">Continue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportAnalytics;
